"use client";

import { useContact } from "@/hooks/usePublicData";
import dynamic from "next/dynamic";

const PropertiesMap = dynamic(() => import("@/components/properties/PropertiesMap"), {
  ssr: false,
  loading: () => <div className="h-full w-full animate-pulse bg-clay-700/40" />,
});

function Icon({ name, className = "", fill = false }: { name: string; className?: string; fill?: boolean }) {
  return <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>{name}</span>;
}

export default function HelpContactCard({ isAr }: { isAr: boolean }) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const { data: contact } = useContact();

  const email = contact?.emails?.[0] || "support@diriyahhomes.sa";
  const phone = contact?.phones?.[0]?.number || "+966 55 000 0000";
  const whatsapp =
    contact?.socials?.find((s) => /whats/i.test(s.platform))?.url ||
    (contact?.phones?.[1]?.number ?? contact?.phones?.[0]?.number) ||
    "+966 55 000 0000";
  const waHref = whatsapp.startsWith("http")
    ? whatsapp
    : `https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`;

  const addr = contact?.address
    ? isAr
      ? contact.address.ar
      : contact.address.en
    : t("حي البجيري، الدرعية، المملكة العربية السعودية", "Al-Bujairi, Diriyah, Saudi Arabia");

  const loc = contact?.location || { lat: 24.7370, lng: 46.5750, address: addr };
  const mapItem = [{ title: { ar: addr, en: addr }, location: loc }];

  const channels = [
    { icon: "chat", label: t("واتساب", "WhatsApp"), value: whatsapp, href: waHref },
    { icon: "mail", label: t("البريد الإلكتروني", "Email"), value: email, href: `mailto:${email}` },
    { icon: "call", label: t("الهاتف", "Phone"), value: phone, href: `tel:${phone.replace(/\s/g, "")}` },
  ];

  return (
    <div className="space-y-6">
      {/* Quick contact — clay card */}
      <div className="rounded-3xl bg-clay-600 text-white p-6">
        <h3 className="font-display text-lg font-bold mb-1">{t("هل تحتاج إلى تواصل سريع؟", "Need to reach us fast?")}</h3>
        <p className="text-sm text-white/70 mb-5">{t("خدمة العملاء متاحة على مدار الساعة 24/7", "Customer support is available 24/7")}</p>

        <div className="space-y-3">
          {channels.map((c) => (
            <a
              key={c.label}
              href={c.href}
              target={c.icon === "chat" ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-2xl bg-white/10 hover:bg-white/15 transition-colors p-3"
            >
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <Icon name={c.icon} fill className="text-[20px]" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-white/70">{c.label}</p>
                <p className="font-bold truncate" dir="ltr">{c.value}</p>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-2 text-sm text-white/80">
          <Icon name="schedule" className="text-[18px]" />
          {contact?.workingHours
            ? isAr
              ? contact.workingHours.ar
              : contact.workingHours.en
            : t("متوسط الرد خلال دقائق", "Average reply within minutes")}
        </div>
      </div>

      {/* Location map */}
      <div className="rounded-3xl border border-clay-100 bg-surface overflow-hidden">
        <div className="p-5 pb-3">
          <h4 className="font-bold text-ink-900 flex items-center gap-2">
            <Icon name="location_on" fill className="text-clay-600 text-[20px]" />
            {t("موقعنا في الدرعية", "Our location in Diriyah")}
          </h4>
          <p className="text-xs text-ink-500 mt-1">{addr}</p>
        </div>
        <div className="h-52">
          <PropertiesMap properties={mapItem} locale={isAr ? "ar" : "en"} isAr={isAr} />
        </div>
      </div>
    </div>
  );
}
